import React from 'react';
import { Row, Col, Modal, Form } from 'react-bootstrap';
import Select from 'react-select';
import ExpandingTextArea from 'react-expanding-textarea';
import moment from 'moment';

// Services & Helpers
import GlobalStateService from 'services/GlobalStateService';
import TodoService from 'services/TodoService';
import UserService from 'services/UserService';
import BookingService from 'services/BookingService';
import TextHelpers from 'helpers/TextHelpers';
import BootboxHelper from 'helpers/BootboxHelper';
import DateHelpers from 'helpers/DateHelpers';

// Components
import SearchBox from 'components/common/SearchBox';
import Loader from 'components/common/Loader';
import DatePicker from 'components/common/DatePicker';
import EditCommentModal from 'components/todos/EditCommentModal'; 

//-------------------------------------------------------------------------------------------------------------------

class EditTodoModal extends React.Component {

    constructor(props) {
        super(props);

        this.editCommentModalRef = React.createRef();

        this.state = {
            isOpen: false
        };
    }

    show(opts) {
        return new Promise((resolve, reject) => {
            this.opts = opts;
            this.todoID = opts.todoID;
            this.resolve = resolve;
            this.loginInfo = GlobalStateService.getValue('loginInfo');

            this.setState({
                isOpen: true,
                isLoading: true
            });
            this.load();
        });
    }

    close() {
        this.setState({
            isOpen: false
        });
    }

    async load() {
        // Get user list
        let users = await UserService.list({
            role: 'Admin'
        });
        users = [{
            id: null,
            firstName: '(Anyone)',
            lastName: ''
        }].concat(users);

        // Load or create todo
        let todo;
        if (this.todoID) {
            todo = await TodoService.get(this.todoID);
        } else {
            todo = {
                ...this.opts,
                comments: []
            };
            if (!todo.bookingType) {
                switch (todo.type) {
                    case 'Booking':
                        todo.bookingType = 'Enquiry';
                        break;
                }
            }
        }

        // Update UI
        this.setState({
            todo,
            users,
            isLoading: false,
            isLoadingBooking: true
        }, () => {
            this.loadBooking();
        });
    }

    async loadBooking() {
        const { bookingID } = this.state.todo;
        if (bookingID) {
            const booking = await BookingService.get(bookingID);
            this.setState({ booking });
        } else {
            this.setState({ booking: null });
        }
        this.setState({ isLoadingBooking: false });
    }

    updateFields(values) {
        const todo = { ...this.state.todo };
        let loadBooking = false;
        for (var field in values) {
            let value = values[field];

            if (value != todo[field]) {
                todo[field] = value;
                if (!todo.changedFields) {
                    todo.changedFields = {};
                }
                todo.changedFields[field] = true;
                
                switch (field) {
                    case 'action':
                        todo.isCompleted = !!todo.action;
                        todo.changedFields.isCompleted = true;
                        break;
                    case 'bookingID':
                        loadBooking = true;
                        break;
                }
            }
        }
        this.setState({
            todo,
            isLoadingBooking: loadBooking
        }, () => {
            if (loadBooking) {
                this.loadBooking();
            }
        });
    }

    async saveAndClose() {
        const { todo } = this.state;
        this.setState({ isLoading: true });
        if (todo.changedFields && Object.keys(todo.changedFields).length > 0) {
            await TodoService.save(todo);
        }
        this.close();
        this.resolve();
    }

    async confirmDelete() {
        const confirm = await BootboxHelper.confirm(`Are you sure you want to delete this to-do?`);
        if (confirm) {
            this.delete();
        }
    }

    async delete() {
        this.setState({ isLoading: true });
        await TodoService.delete(this.state.todo.id);
        this.close();
        this.resolve();
    }

    async addComment() {
        const { todo } = this.state;
        await this.editCommentModalRef.current.show({
            todoID: todo.id
        });
        this.load();
    }

    async editComment(id) {
        await this.editCommentModalRef.current.show({
            commentID: id
        });
        this.load();
    }

    render() {
        const {
            isOpen
        } = this.state;

        if (!isOpen) {
            return null;
        }

        return (<>
            <Modal show className="edit-todo-modal">
                {this.renderInner()}
            </Modal>

            <EditCommentModal ref={this.editCommentModalRef} />
        </>);
    }

    renderInner() {
        const {
            isLoading,
            todo
        } = this.state;

        if (isLoading) {
            return (
                <Modal.Body>
                    <Loader fullPage />
                </Modal.Body>
            );
        }

        return (

            <form onSubmit={e => { e.preventDefault(); this.saveAndClose() }}>
                <Modal.Body>

                    <Row>

                        <Col md="8">

                            {this.renderLeftCol()}

                        </Col>

                        <Col md="4">

                            {this.renderRightCol()}

                        </Col>

                    </Row>

                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary me-auto" onClick={() => this.close()}>
                        Cancel
                    </button>
                    {todo.id &&
                        <button type="button" className="btn btn-danger ms-auto me-auto" onClick={() => this.confirmDelete()}>
                            Delete
                        </button>
                    }
                    <button type="submit" className="btn btn-primary ms-auto">
                        Save
                    </button>
                </Modal.Footer>
            </form>
        );
    }

    renderLeftCol() {
        const {
            todo,
            users
        } = this.state;

        return (<>

            {todo.type != 'Booking' &&
                <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    {todo.type == 'Custom' ?
                        <ExpandingTextArea
                            className="form-control"
                            value={todo.description || ''}
                            rows="2"
                            onChange={e => this.updateFields({ description: e.target.value })}
                        /> :
                        <div className="form-control-plaintext">
                            {TextHelpers.splitIntoLines(todo.description, line => <div>{line}</div>)}
                        </div>
                    }
                </Form.Group>
            }

            {todo.type && <>
                {this.renderTypeSpecificFields()}
                <hr />
            </>}

            <Row>

                <Col md="4">

                    <Form.Group className="">
                        <Form.Label>Assigned to</Form.Label>
                        <Select
                            className="react-select-container"
                            classNamePrefix="react-select"
                            value={users.find(c => c.id == todo.assignedToUserID)}
                            options={users}
                            getOptionValue={c => c.id}
                            getOptionLabel={c => `${c.firstName || ''} ${c.lastName || ''}`}
                            onChange={v => this.updateFields({ assignedToUserID: v.id })}
                            isDisabled={todo.isCompleted}
                        />
                    </Form.Group>

                </Col>

                <Col md="3">

                    <Form.Group className="">
                        <Form.Label>Due date</Form.Label>
                        <DatePicker
                            className="form-control"
                            selected={todo.dateDue ? new Date(todo.dateDue) : null}
                            onChange={value => this.updateFields({ dateDue: value })}
                            disabled={todo.isCompleted}
                        />
                    </Form.Group>

                </Col>

                <Col md="2">

                    {!!todo.id &&
                        <Form.Group className="">
                            <Form.Label>Completed</Form.Label>
                            <Form.Check
                                type="checkbox"
                                checked={todo.isCompleted}
                                id="is-completed"
                                onChange={e => this.updateFields({ isCompleted: e.target.checked })}
                                label="Completed"
                            />
                        </Form.Group>
                    }

                </Col>

                <Col md="3">

                    {todo.type == 'Booking' &&
                        <Form.Group className="">
                            <Form.Label>Visible to vendor</Form.Label>
                            <Form.Check
                                type="checkbox"
                                checked={todo.isVendorVisible}
                                id="is-vendor-visible"
                                onChange={e => this.updateFields({ isVendorVisible: e.target.checked })}
                                label="Visible to vendor"
                            />
                        </Form.Group>
                    }

                </Col>

            </Row>

        </>);
    }

    renderRightCol() {
        const {
            todo
        } = this.state;

        return (<>

            {!!this.todoID && <>

                <button type="button" className="btn btn-secondary mb-3" onClick={() => this.addComment()}>
                    Add comment
                </button>

                <table className="table table-bordered comments-table mb-0">
                    <thead>
                        <tr>
                            <th>Comments</th>
                        </tr>
                    </thead>
                    <tbody>
                        {todo.comments.map(c =>
                            <tr key={c.id}>
                                <td>
                                    <div>
                                        <b>[{c.userName}, {moment(c.dateTime).format('DD/MM/YYYY')}]</b><br />
                                        {TextHelpers.splitIntoLines(c.text, line => <div>{line}</div>)}

                                        {c.isUserMe &&
                                            <button type="button" className="btn btn-sm btn-secondary edit-btn" onClick={() => this.editComment(c.id)}>
                                                <span className="fa fa-pencil-alt" />
                                            </button>
                                        }
                                        
                                    </div>
                                </td>
                            </tr>
                        )}
                        {todo.comments.length == 0 &&
                            <tr>
                                <td colSpan="1" className="empty-text">
                                    No comments yet
                                </td>
                            </tr>
                        }
                    </tbody>
                </table>
                
            </>}

        </>);
    }

    renderTypeSpecificFields() {
        switch (this.state.todo.type) {
            case 'ApproveAmendments':
                return this.renderApproveAmendmentsFields();
            case 'Booking':
                return this.renderBookingFields();
        }
    }

    renderApproveAmendmentsFields() {
        const {
            todo
        } = this.state;
        if (!todo.amendmentBatch) {
            return null;
        }
        const {
            instructor,
            requestedByUser,
            dateRequested,
            addedDates,
            removedDates,
            requestNotes
        } = this.state.todo.amendmentBatch;

        const numAddedDates = Object.keys(addedDates).length;
        const numRemovedDates = Object.keys(removedDates).length;
        const addedDateRuns = DateHelpers.getDateRuns(addedDates);
        const removedDateRuns = DateHelpers.getDateRuns(removedDates);

        return (<>

            <Row>

                <Col md="4">
                    
                    <Form.Group className="mb-3">
                        <Form.Label>Instructor</Form.Label>
                        <p className="form-control-plaintext">
                            {TextHelpers.formatName(
                                instructor.firstName,
                                instructor.lastName
                            )}
                        </p>
                    </Form.Group>

                </Col>

                {requestedByUser && (requestedByUser.firstName != instructor.firstName || requestedByUser.lastName != instructor.lastName) &&
                    <Col md="4">

                        <Form.Group className="mb-3">
                            <Form.Label>Requested by</Form.Label>
                            <p className="form-control-plaintext">
                                {TextHelpers.formatName(
                                    requestedByUser.firstName,
                                    requestedByUser.lastName
                                )}
                            </p>
                        </Form.Group>
                        
                    </Col>
                }

                <Col md="4">

                    <Form.Group className="mb-3">
                        <Form.Label>Date of request</Form.Label>
                        <p className="form-control-plaintext">
                            {moment(dateRequested).format('DD/MM/YYYY')}
                        </p>
                    </Form.Group>

                </Col>

            </Row>

            <div className="mb-3">
                {instructor.mobileNumber &&
                    <a href={'https://web.whatsapp.com/send?phone=' + TextHelpers.getPhoneNumberNumeric(instructor.mobileNumber) + '&text&app_absent=0'} target="_blank" className="btn btn-secondary btn-sm me-1">
                        <span className="fab fa-whatsapp"></span>{' '}
                        WhatsApp
                    </a>
                }
                {instructor.email &&
                    <a href={'mailto:' + instructor.email} target="_blank" className="btn btn-secondary btn-sm">
                        <span className="fa-solid fa-envelope"></span>{' '}
                        Email
                    </a>
                }
            </div>

            <Row>

                <Col md="4">

                    <Form.Group className="mb-3">
                        <Form.Label>Added ({numAddedDates})</Form.Label>
                        <p className="form-control-plaintext">
                            {addedDateRuns.length > 0 ?
                                addedDateRuns.map((r, index) => <div key={index}>{DateHelpers.formatDateRun(r)}</div>) :
                                '(None)'
                            }
                        </p>
                    </Form.Group>

                </Col>

                <Col md="4">

                    <Form.Group className="mb-3">
                        <Form.Label>Removed ({numRemovedDates})</Form.Label>
                        <div className="form-control-plaintext">
                            {removedDateRuns.length > 0 ?
                                removedDateRuns.map((r, index) => <div key={index}>{DateHelpers.formatDateRun(r)}</div>) :
                                '(None)'
                            }
                        </div>
                    </Form.Group>

                </Col>

                <Col md="4">

                    {requestNotes &&
                        <Form.Group className="mb-3">
                            <Form.Label>Notes</Form.Label>
                            <div className="form-control-plaintext">
                                {TextHelpers.splitIntoLines(requestNotes, line => <div>{line}</div>)}
                            </div>
                        </Form.Group>
                    }

                </Col>

            </Row>

            <Form.Group className="mb-3">
                <Form.Label>Action</Form.Label>
                <Form.Check
                    id="no-action"
                    type="radio"
                    checked={!todo.action}
                    onChange={e => this.updateFields({ action: '' })}
                    label="No action"
                />
                <Form.Check
                    id="approve-amends"
                    type="radio"
                    checked={todo.action == 'approve'}
                    onChange={e => this.updateFields({ action: 'approve' })}
                    label="Approve amendments"
                />
                <Form.Check
                    id="decline-amends"
                    type="radio"
                    checked={todo.action == 'decline'}
                    onChange={e => this.updateFields({ action: 'decline' })}
                    label="Decline amendments"
                />
            </Form.Group>

        </>);
    }

    renderBookingFields() {
        const {
            todo,
            booking,
            isLoadingBooking
        } = this.state;

        return (<>

            <Row>
            
                <Col md="4">

                    <Form.Group className="mb-3">
                        <Form.Label>Type</Form.Label>
                        <select
                            className="form-control"
                            value={todo.bookingType || ''}
                            onChange={e => this.updateFields({ bookingType: e.target.value })}
                        >
                            <option value="Enquiry">Enquiry</option>
                            <option value="HoldTentative">Hold / Tentative</option>
                            <option value="Active">Active</option>
                        </select>
                    </Form.Group>

                </Col>

                <Col md="4">

                    <Form.Group className="mb-3">
                        <Form.Label>Link to booking</Form.Label>
                        {!!todo.bookingID ? <>
                            {isLoadingBooking ? <Loader isInline /> : <>
                                {booking.lmsid} ({booking.status}){' '}
                                <button type="button" className="btn btn-danger btn-sm" onClick={() => this.updateFields({ bookingID: null })}>
                                    <span className="fa fa-times" />
                                </button>
                            </>}
                        </> :
                            <SearchBox
                                types={['Booking']}
                                onSelect={booking => {
                                    const bookingID = booking.id.split('-')[1];
                                    this.updateFields({ bookingID })
                                }}
                            />
                        }
                    </Form.Group>

                </Col>

                <Col md="4">

                    {todo.bookingType == 'Enquiry' &&
                        <Form.Group className="mb-3">
                            <Form.Label>Requester</Form.Label>
                            <input
                                type="text"
                                className="form-control"
                                value={todo.bookingRequester || ''}
                                onChange={e => this.updateFields({ bookingRequester: e.target.value })}
                            />
                        </Form.Group>
                    }

                </Col>

            </Row>

            <Row>

                <Col md="4">
                
                    <Form.Group className="mb-3">
                        <Form.Label>LMS ID / Status</Form.Label>
                        <input
                            type="text"
                            className="form-control"
                            value={todo.bookingLMSIDOrStatus || ''}
                            onChange={e => this.updateFields({ bookingLMSIDOrStatus: e.target.value })}
                        />
                    </Form.Group>

                </Col>

                <Col md="4">

                    <Form.Group className="mb-3">
                        <Form.Label>Instructor</Form.Label>
                        <input
                            type="text"
                            className="form-control"
                            value={todo.bookingInstructor || ''}
                            onChange={e => this.updateFields({ bookingInstructor: e.target.value })}
                        />
                    </Form.Group>

                </Col>

                <Col md="4">

                    <Form.Group className="mb-3">
                        <Form.Label>Dates</Form.Label>
                        <input
                            type="text"
                            className="form-control"
                            value={todo.bookingDates || ''}
                            onChange={e => this.updateFields({ bookingDates: e.target.value })}
                        />
                    </Form.Group>

                </Col>

            </Row>

            <Row>

                <Col md="4">

                    <Form.Group className="mb-3">
                        <Form.Label>Course</Form.Label>
                        <input
                            type="text"
                            className="form-control"
                            value={todo.bookingCourse || ''}
                            onChange={e => this.updateFields({ bookingCourse: e.target.value })}
                        />
                    </Form.Group>

                </Col>

                <Col md="4">

                    <Form.Group className="mb-3">
                        <Form.Label>Customer</Form.Label>
                        <input
                            type="text"
                            className="form-control"
                            value={todo.bookingCustomer || ''}
                            onChange={e => this.updateFields({ bookingCustomer: e.target.value })}
                        />
                    </Form.Group>

                </Col>

                <Col md="4">

                    <Form.Group className="mb-3">
                        <Form.Label>Language</Form.Label>
                        <input
                            type="text"
                            className="form-control"
                            value={todo.bookingLanguage || ''}
                            onChange={e => this.updateFields({ bookingLanguage: e.target.value })}
                        />
                    </Form.Group>

                </Col>
                
            </Row>

            <Row>

                <Col md="4">

                    <Form.Group className="mb-3">
                        <Form.Label>vILT / ILT</Form.Label>
                        <select
                            className="form-control"
                            value={todo.bookingModality || ''}
                            onChange={e => this.updateFields({ bookingModality: e.target.value })}
                        >
                            <option value="">(Select)</option>
                            <option value="vILT">vILT</option>
                            <option value="ILT">ILT</option>
                        </select>
                    </Form.Group>

                </Col>

                <Col md="8">

                    <Form.Group className="mb-3">
                        <Form.Label>Address</Form.Label>
                        <input
                            type="text"
                            className="form-control"
                            value={todo.bookingAddress || ''}
                            onChange={e => this.updateFields({ bookingAddress: e.target.value })}
                        />
                    </Form.Group>

                </Col>

            </Row>

        </>);
    }
}

export default EditTodoModal;